// =============================================================================
// SIREPPAR - Partidos integrantes de una alianza (tab Documentación)
// =============================================================================
// Va ARRIBA del tablero de documentación porque es lo primero que hay que
// resolver: si un integrante no está en SIREPPAR, alguien de la Secretaría tiene
// que corroborarlo a mano contra el registro antes de que el expediente pueda
// elevarse al Secretario (el backend bloquea la elevación mientras quede uno sin
// verificar).
//
// Los integrantes que SÍ están en SIREPPAR no muestran el botón: sobre un partido
// registrado no hay nada que verificar, y dejar el botón ahí sugeriría que el dato
// del propio sistema es dudoso.
// =============================================================================

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { alianzaGestionService } from '../../../services/api';

export interface IntegranteAlianza {
  id: string;
  enSireppar: boolean;
  partidoId: string | null;
  denominacion: string | null;
  sigla: string | null;
  numero: number | null;
  tipo: string | null;
  localidad: string | null;
  estadoPartido: string | null;
  actaVoluntad: { id: string; nombreOriginal: string; url: string | null } | null;
  requiereVerificacion: boolean;
  verificadoEn: string | null;
  verificadoPorNombre: string | null;
}

interface Props {
  expedienteId: string;
  integrantes: IntegranteAlianza[];
  /** Sólo los roles de análisis pueden marcar verificado, y sólo en análisis. */
  puedeVerificar: boolean;
}

const TIPO_LABEL: Record<string, string> = {
  MUNICIPAL: 'Municipal',
  DISTRITO: 'Distrito',
  PROVINCIAL: 'Provincial',
  NACIONAL: 'Nacional',
};

const fmtFecha = (iso: string) =>
  new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });

export default function PanelIntegrantesAlianza({ expedienteId, integrantes, puedeVerificar }: Props) {
  const queryClient = useQueryClient();

  const verificarMutation = useMutation({
    mutationFn: (integranteId: string) =>
      alianzaGestionService.verificarIntegrante(expedienteId, integranteId),
    onSuccess: () => {
      toast.success('Integrante verificado');
      queryClient.invalidateQueries({ queryKey: ['alianza-expediente', expedienteId] });
    },
    onError: (e: any) =>
      toast.error(e.response?.data?.error?.message || 'No se pudo marcar como verificado'),
  });

  const pendientes = integrantes.filter((i) => i.requiereVerificacion && !i.verificadoEn);

  return (
    <div className="mb-8">
      <div className="flex items-baseline justify-between border-b pb-2 mb-4">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
          Partidos integrantes ({integrantes.length})
        </h4>
        {pendientes.length > 0 && (
          <span className="text-xs font-semibold text-amber-700">
            {pendientes.length} sin verificar
          </span>
        )}
      </div>

      <div className="space-y-2">
        {integrantes.map((i) => (
          <div
            key={i.id}
            className="flex flex-wrap items-start justify-between gap-3 border border-gray-200 rounded-lg p-3"
          >
            <div className="min-w-0 flex-1">
              {/* Los de SIREPPAR llevan link al detalle del partido; los "otros" van
                  en texto plano con el tipo y la localidad declarados, que es todo
                  lo que el sistema sabe de ellos. */}
              {i.enSireppar && i.partidoId ? (
                <Link
                  to={`/partidos/${i.partidoId}`}
                  className="font-medium text-blue-600 hover:text-blue-700 break-words"
                >
                  {i.denominacion}
                </Link>
              ) : (
                <p className="font-medium text-gray-900 break-words">{i.denominacion}</p>
              )}
              <p className="text-xs text-gray-500 mt-0.5">
                {[
                  i.sigla,
                  i.tipo ? TIPO_LABEL[i.tipo] || i.tipo : null,
                  i.localidad,
                  i.enSireppar ? `N° ${i.numero}` : 'Declarado — no registrado en SIREPPAR',
                ].filter(Boolean).join(' · ')}
              </p>

              <div className="mt-2">
                {i.actaVoluntad ? (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-600">Constancia de la voluntad de aliarse</span>
                    {i.actaVoluntad.url ? (
                      <a
                        href={i.actaVoluntad.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline font-medium"
                      >
                        Ver
                      </a>
                    ) : (
                      <span className="text-gray-400">(archivo no disponible)</span>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-red-600 font-medium">Sin constancia de la voluntad de aliarse</p>
                )}
              </div>
            </div>

            <div className="flex-shrink-0">
              {/* VERDE = está en SIREPPAR o ya se verificó: no hay nada que hacer.
                  ROJO = falta corroborarlo a mano, y además es lo que traba la
                  elevación al Secretario. El color tiene que decir de un vistazo
                  cuál de los dos casos es. */}
              {!i.requiereVerificacion ? (
                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                  ✓ Registrado en SIREPPAR
                </span>
              ) : i.verificadoEn ? (
                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 block">
                  ✓ Verificado por {i.verificadoPorNombre || 'la Secretaría'} el {fmtFecha(i.verificadoEn)}
                </span>
              ) : puedeVerificar ? (
                <button
                  onClick={() => verificarMutation.mutate(i.id)}
                  disabled={verificarMutation.isPending}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {verificarMutation.isPending ? 'Verificando...' : 'Marcar como verificado'}
                </button>
              ) : (
                <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                  Pendiente de verificación
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
